import { useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useLanguageCode } from "../lib/activeLanguage";
import { useIsAdmin } from "../lib/currentUser";
import { FLAGS, setFlag, useFlag } from "../lib/featureFlags";
import { masteryBar } from "../lib/palette";

type ActionCandidateRow = {
  skillCode: string;
  skillName: string;
  exerciseId: number;
  scaffoldingLevel: number;
  priority: number;
  infoGainBits: number;
  learningProgress: number;
  expectedRewardBits: number;
  qHat: number | null;
  isChosen: boolean;
};

type SchedulerRationale = {
  infoGainBits: number;
  learningProgress: number;
  retentionTimeGainedDays: number;
  overdue: boolean;
  winningSignal: string;
  qHat?: number | null;
  vHat?: number | null;
  actionSpace?: readonly ActionCandidateRow[];
};

// Module-level cache of rationales from /exercises/next, keyed by the
// exerciseId that was picked. The debug overlay on the landing page reads
// from here. The cache is short-lived (in memory) and only ever populated
// when the debug flag is on — no privacy implications.
const rationaleCache: Map<number, SchedulerRationale> = new Map();

const SCAFFOLD_LABEL: Record<number, string> = {
  0: "opt + hint",
  1: "options",
  2: "hint",
  3: "cold",
};

export const Route = createFileRoute("/practice/$exerciseId")({
  component: PracticeExercise,
});

type BeliefSnapshot = {
  mu: number;
  sigma: number;
  cefrCenter: number;
  cefrLow: number;
  cefrHigh: number;
  caption: string;
};

type SkillUpdate = {
  skillCode: string;
  skillName: string;
  retrievabilityBefore: number;
  retrievabilityAfter: number;
  stabilityBefore: number;
  stabilityAfter: number;
  nextDueAt: string | null;
  infoGainBits: number;
  passed: boolean;
  // Optional — populated once the backend redeploys with the BeliefSnapshot
  // additions. `undefined` on legacy responses; `null` when the per-skill
  // belief update failed (best-effort in grader.py).
  beliefBefore?: BeliefSnapshot | null;
  beliefAfter?: BeliefSnapshot | null;
};

type TransferFlag = {
  patternSlug: string;
  description: string;
  note: string | null;
};

type LexemeUpdate = {
  lemma: string;
  pos: string;
  role: string;
  retrievabilityBefore: number;
  retrievabilityAfter: number;
  stabilityBefore: number;
  stabilityAfter: number;
  nextDueAt: string | null;
  passed: boolean;
};

type ConstructionUpdate = {
  form: string;
  retrievabilityBefore: number;
  retrievabilityAfter: number;
  stabilityBefore: number;
  stabilityAfter: number;
  nextDueAt: string | null;
  passed: boolean;
};

type GradingResult = {
  attemptId: number;
  correct: boolean;
  response: string;
  expected: string;
  feedback: string | null;
  infoGainBits: number;
  retentionTimeGainedDays: number;
  skillUpdates: readonly SkillUpdate[];
  lexemeUpdates?: readonly LexemeUpdate[];
  constructionUpdates?: readonly ConstructionUpdate[];
  transferFlags?: readonly TransferFlag[];
};

type ExerciseMetadata = {
  instruction?: string;
  sentence?: string;
  // true_false exercises put their statement here instead of `sentence`.
  // Treated as sentence-equivalent by the Prompt component.
  claim?: string;
  gloss?: string;
  hint?: string;
  options?: string[];
  tokens?: string[];
  // contrast-specific
  a_sentence?: string;
  b_sentence?: string;
  gloss_a?: string;
  gloss_b?: string;
  // Older contrast seeds stored forms as `["mir", "mich"]`; newer ones
  // store `[{label, value, meaning, ...}]`. ContrastInput normalises
  // both shapes to the bare `value` strings before passing them to
  // StyledPicker — which renders strings only, so passing the object
  // form straight through throws React #31.
  forms?: ReadonlyArray<string | { value: string }>;
  // cloze-passage specific
  passage?: string;
  num_blanks?: number;
};

function PracticeExercise() {
  const { exerciseId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const language = useLanguageCode();
  const [response, setResponse] = useState("");
  const [lastResult, setLastResult] = useState<GradingResult | null>(null);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const strictUmlauts = useFlag("strictUmlauts");
  // Debug overlay: gated on `?debug=1` URL param OR the historical
  // `debugScheduler` flag. URL gating means devs can opt in per-tab
  // without changing global flag state, and learners on the default
  // route never see the instrumentation.
  const debugFlag = useFlag("debugScheduler");
  const debugScheduler =
    debugFlag ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("debug") === "1");
  const isAdmin = useIsAdmin();

  // Wall-clock when this exercise first rendered. Reset on every new
  // exerciseId so the submit carries a meaningful response_time_ms.
  const displayedAt = useRef<number>(Date.now());
  useEffect(() => {
    displayedAt.current = Date.now();
  }, [exerciseId]);

  // Reset parent-level state whenever the URL exerciseId changes.
  // onNext already clears these before navigating, but navigation
  // can also happen via the Practice nav link, back-button, or any
  // path that bypasses onNext — without this useEffect, the new
  // exercise would render the previous one's lastResult/response.
  useEffect(() => {
    setResponse("");
    setLastResult(null);
    setAnswerRevealed(false);
  }, [exerciseId]);

  // Auto-advance: when on, after the user gets the feedback we wait a
  // short, result-dependent delay and then call onNext for them. Any
  // tap / click / keypress cancels (and the manual "Next" button still
  // works at any time). Skip the auto-advance for the "Show answer"
  // flow because that's an "I'm studying this one" state.
  const autoAdvance = useFlag("autoAdvance");
  const [autoAdvanceMs, setAutoAdvanceMs] = useState<number | null>(null);

  const exerciseQ = useQuery({
    queryKey: ["exercise", exerciseId],
    queryFn: async () => {
      const { data, error } = await api.GET("/exercises/{exercise_id}", {
        params: { path: { exercise_id: Number(exerciseId) } },
      });
      if (error) throw error;
      return data;
    },
    // The /exercises/next call already populates this cache key
    // before navigation (see beforeLoad in practice.tsx and onNext
    // below). staleTime keeps that primed value fresh through the
    // remount so React Query doesn't background-refetch the slow
    // scheduler endpoint we just navigated away from.
    staleTime: 60_000,
  });

  // Holds the id of the prefetched "next exercise". We fire the
  // /exercises/next fetch in submitM.onSuccess (right when the
  // feedback panel appears) so by the time the user finishes reading
  // their grade and clicks Next, the scheduler's work is already done
  // and navigation is instant. The full payload is primed into React
  // Query's cache via setQueryData, so we only need the id here to
  // navigate.
  const prefetchedNextIdRef = useRef<number | null>(null);

  const submitM = useMutation({
    mutationFn: async (args: { response: string }) => {
      const scaffoldingLevel = exerciseQ.data?.scaffoldingLevel ?? -1;
      const responseTimeMs = Math.max(0, Date.now() - displayedAt.current);
      const { data, error } = await api.POST(
        "/exercises/{exercise_id}/submit",
        {
          params: { path: { exercise_id: Number(exerciseId) } },
          body: {
            response: args.response,
            strictUmlauts,
            scaffoldingLevel,
            responseTimeMs,
          },
        },
      );
      if (error) throw error;
      return data as GradingResult;
    },
    onSuccess: (result) => {
      setLastResult(result);
      // Keep the response in the input visible after submit so the user can
      // see what they typed / selected. Don't clear it here.
      queryClient.invalidateQueries({ queryKey: ["skills-mastery"] });
      // Fire the next-exercise fetch NOW, while the user is reading
      // the feedback. By the time they click Next (or hit Enter),
      // the payload is already on the wire / done. The slow path is
      // /exercises/next itself, but it runs in parallel with the
      // user reading; net perceived latency on Next ≈ 0.
      api
        .GET("/exercises/next", {
          params: {
            query: { language, exclude: Number(exerciseId) },
          },
        })
        .then(({ data }) => {
          if (data?.exercise.id != null) {
            prefetchedNextIdRef.current = data.exercise.id;
            // Also prime React Query so the destination route's
            // queryFn finds the value already there.
            queryClient.setQueryData(
              ["exercise", String(data.exercise.id)],
              data,
            );
            if (data.rationale) {
              rationaleCache.set(
                data.exercise.id,
                data.rationale as unknown as SchedulerRationale,
              );
            }
          }
        })
        .catch(() => {
          /* fall through; onNext will fetch synchronously */
        });
    },
  });

  const onNext = async () => {
    setLastResult(null);
    setResponse("");
    setAnswerRevealed(false);
    setAutoAdvanceMs(null);

    // Fast path: the prefetch in submitM.onSuccess already landed.
    const prefetchedId = prefetchedNextIdRef.current;
    prefetchedNextIdRef.current = null;
    if (prefetchedId != null) {
      navigate({
        to: "/practice/$exerciseId",
        params: { exerciseId: String(prefetchedId) },
      });
      return;
    }

    // Slow path: prefetch didn't finish in time (or user skipped
    // without submitting). Fetch synchronously, then navigate.
    try {
      const { data, error } = await api.GET("/exercises/next", {
        params: {
          query: {
            language,
            exclude: Number(exerciseId),
          },
        },
      });
      if (error) throw error;
      if (data?.exercise.id != null) {
        if (data.rationale) {
          rationaleCache.set(
            data.exercise.id,
            data.rationale as unknown as SchedulerRationale,
          );
        }
        queryClient.setQueryData(["exercise", String(data.exercise.id)], data);
        navigate({
          to: "/practice/$exerciseId",
          params: { exerciseId: String(data.exercise.id) },
        });
        return;
      }
    } catch {
      /* fall through */
    }
    // Fallback: go to the redirector which will pick something.
    navigate({ to: "/practice" });
  };

  // Auto-advance timer. Fires when lastResult lands AND the flag is on
  // AND the user didn't take the "Show answer" path (where they're
  // explicitly studying the item). Cancels on any tap/click/keydown so
  // the learner stays in control: any interaction reverts to the
  // manual Next button. The Feedback panel reads `autoAdvanceMs` to
  // animate a countdown bar.
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  useEffect(() => {
    if (!autoAdvance || !lastResult || answerRevealed) {
      setAutoAdvanceMs(null);
      return;
    }
    const delay = lastResult.correct ? 2000 : 4000;
    setAutoAdvanceMs(delay);
    const timer = window.setTimeout(() => {
      void onNextRef.current();
    }, delay);
    const cancel = () => {
      window.clearTimeout(timer);
      setAutoAdvanceMs(null);
    };
    const onKey = (e: KeyboardEvent) => {
      // Enter = advance now; everything else cancels.
      if (e.key === "Enter") {
        window.clearTimeout(timer);
        setAutoAdvanceMs(null);
        void onNextRef.current();
      } else {
        cancel();
      }
    };
    window.addEventListener("pointerdown", cancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", cancel);
      window.removeEventListener("keydown", onKey);
    };
  }, [autoAdvance, lastResult, answerRevealed]);

  if (exerciseQ.isLoading) return <Centered>Loading…</Centered>;
  if (exerciseQ.isError || !exerciseQ.data) {
    return <Centered>Exercise not found.</Centered>;
  }

  // When navigating between exercises, React Query keeps the previous
  // exercise's data until the new fetch resolves. Showing it would flash
  // the wrong prompt/scaffolding type (e.g. multiple-choice buttons that
  // disappear a moment later). Wait for the loaded data to match the URL.
  if (String(exerciseQ.data.exercise.id) !== exerciseId) {
    return <Centered>Loading…</Centered>;
  }

  const {
    exercise,
    primarySkillCode,
    primarySkillName,
    primarySkillRetrievability,
    primarySkillStability,
    scaffoldingLevel,
    primarySkillBeliefSigma,
  } = exerciseQ.data as typeof exerciseQ.data & {
    primarySkillBeliefSigma?: number | null;
  };
  const meta = (exercise.metadata ?? {}) as ExerciseMetadata;

  const showOptions =
    Array.isArray(meta.options) &&
    (scaffoldingLevel === 0 || scaffoldingLevel === 1);
  const showHint =
    typeof meta.hint === "string" &&
    (scaffoldingLevel === 0 || scaffoldingLevel === 2);

  const rationale: SchedulerRationale | null =
    (exerciseQ.data.rationale as unknown as
      | SchedulerRationale
      | null
      | undefined) ??
    rationaleCache.get(Number(exerciseId)) ??
    null;

  // Multi-blank detection: when the sentence contains 2+ `___` slots
  // (e.g. "Ich lerne Deutsch, ___ ich in Berlin ___."), a single text
  // box hides that the question expects multiple ordered answers. We
  // render inline inputs in place of each `___` and skip the
  // duplicate sentence in the Prompt above.
  const sentence = meta.sentence ?? exercise.prompt ?? "";
  const blankCount = sentence.split("___").length - 1;
  const useMultiBlank = blankCount >= 2 && exercise.type === "fill_blank";

  // Match detection: type=match exercises encode pairs as
  // "de:en|de:en|..." in `expected`. A single text box is unusable —
  // render a per-row dropdown UI so the learner picks the English
  // gloss for each German term.
  const matchPairs =
    exercise.type === "match" ? parseMatchPairs(exercise.expected) : null;
  const useMatch = matchPairs !== null && matchPairs.length >= 2;

  // true_false: expected is literally "true" or "false". Render two
  // big buttons.
  const useTrueFalse =
    exercise.type === "true_false" &&
    (exercise.expected.trim().toLowerCase() === "true" ||
      exercise.expected.trim().toLowerCase() === "false");

  // contrast: expected is "A:foo|B:bar" (two labelled slots). Render
  // two dropdowns side by side, one per slot, with all values as
  // options.
  const contrastSlots =
    exercise.type === "contrast"
      ? parseMatchPairs(exercise.expected) /* same A:foo|B:bar format */
      : null;
  const useContrast = contrastSlots !== null && contrastSlots.length === 2;

  // order: metadata.tokens lists scrambled words; expected is the
  // correctly-ordered sentence. Render tap-to-append tokens.
  const orderTokens = Array.isArray(meta.tokens) ? meta.tokens : null;
  const useOrder =
    exercise.type === "order" &&
    orderTokens !== null &&
    orderTokens.length >= 2;

  // cloze_passage: meta.passage has `(N)___` markers (e.g.
  // "Ich (1)___ Maria. Ich (2)___ aus Spanien."), expected is
  // pipe-separated answers in order. Render the passage with
  // inputs in place of each marker.
  const clozePassage = exercise.type === "cloze_passage" ? meta.passage : null;
  const clozeBlankCount =
    clozePassage !== undefined && clozePassage !== null
      ? (clozePassage.match(/\(\d+\)___/g) || []).length
      : 0;
  const useCloze =
    exercise.type === "cloze_passage" &&
    clozePassage !== undefined &&
    clozePassage !== null &&
    clozeBlankCount >= 2;

  return (
    <section className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12">
      <ExerciseHeader
        type={exercise.type}
        optionsShown={showOptions}
        primarySkillCode={primarySkillCode}
        primarySkillName={primarySkillName}
        primarySkillRetrievability={primarySkillRetrievability}
        primarySkillStability={primarySkillStability}
        scaffoldingLevel={scaffoldingLevel}
        isFirstContact={primarySkillStability <= 0}
        guessRate={computeGuessRate(
          exercise.type,
          showOptions,
          meta,
          useTrueFalse,
        )}
        beliefSigma={primarySkillBeliefSigma ?? null}
      />

      {/* For cloze, the input renders the passage + instruction
          itself; otherwise Prompt handles the heading.
          hideSentence MUST track what actually renders below, not just
          the multi-blank candidacy: when a multi-blank fill_blank has
          synthesised MC options (discrete bootstrap), MultipleChoice
          wins the input-renderer priority over MultiBlankInput, and MC
          doesn't show the sentence inline. Without the `&& !showOptions`
          gate the German sentence with the `___` markers disappears
          and the learner sees only the English gloss + 4 buttons. */}
      {!useCloze && (
        <Prompt
          meta={meta}
          fallback={exercise.prompt}
          hideSentence={useMultiBlank && !showOptions}
        />
      )}

      {showHint && !lastResult && (
        <p className="text-sm text-ink-400 mb-6 italic">Hint: {meta.hint}</p>
      )}

      {answerRevealed && !lastResult && (
        <div className="mb-6 rounded-md border border-ink-200 bg-ink-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-ink-400 mb-1">
            Expected
          </div>
          <div className="font-mono text-ink-900">{exercise.expected}</div>
        </div>
      )}

      {lastResult ? (
        <Feedback
          result={lastResult}
          hint={meta.hint}
          onNext={onNext}
          autoAdvanceMs={autoAdvanceMs}
        />
      ) : answerRevealed ? (
        <div className="flex items-center gap-3">
          <button
            onClick={onNext}
            className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors"
          >
            Next exercise
          </button>
          <span className="text-xs text-ink-400">
            Answering is disabled after revealing — this won't count as an
            attempt.
          </span>
        </div>
      ) : showOptions ? (
        <MultipleChoice
          key={exerciseId}
          options={meta.options!}
          disabled={submitM.isPending}
          onPick={(c) => {
            setResponse(c);
            submitM.mutate({ response: c });
          }}
          onKeySelect={(c) => setResponse(c)}
          onKeySubmit={() => {
            if (response) submitM.mutate({ response });
          }}
          selected={response}
        />
      ) : useMultiBlank ? (
        <MultiBlankInput
          key={exerciseId}
          sentence={sentence}
          disabled={submitM.isPending}
          onSubmit={(joined) => {
            if (!joined.trim()) return;
            setResponse(joined);
            submitM.mutate({ response: joined });
          }}
        />
      ) : useMatch ? (
        <MatchInput
          key={exerciseId}
          pairs={matchPairs!}
          disabled={submitM.isPending}
          onSubmit={(joined) => {
            if (!joined.trim()) return;
            setResponse(joined);
            submitM.mutate({ response: joined });
          }}
        />
      ) : useTrueFalse ? (
        <TrueFalseInput
          key={exerciseId}
          disabled={submitM.isPending}
          onPick={(v) => {
            setResponse(v);
            submitM.mutate({ response: v });
          }}
          onKeySelect={(v) => setResponse(v)}
          onKeySubmit={() => {
            if (response) submitM.mutate({ response });
          }}
          selected={response}
        />
      ) : useContrast ? (
        <ContrastInput
          key={exerciseId}
          slots={contrastSlots!}
          meta={meta}
          disabled={submitM.isPending}
          onSubmit={(joined) => {
            if (!joined.trim()) return;
            setResponse(joined);
            submitM.mutate({ response: joined });
          }}
        />
      ) : useOrder ? (
        <OrderInput
          key={exerciseId}
          tokens={orderTokens!}
          disabled={submitM.isPending}
          onSubmit={(joined) => {
            if (!joined.trim()) return;
            setResponse(joined);
            submitM.mutate({ response: joined });
          }}
        />
      ) : useCloze ? (
        <ClozePassageInput
          key={exerciseId}
          passage={clozePassage!}
          disabled={submitM.isPending}
          onSubmit={(joined) => {
            if (!joined.trim()) return;
            setResponse(joined);
            submitM.mutate({ response: joined });
          }}
        />
      ) : (
        <TypedInput
          key={exerciseId}
          value={response}
          onChange={setResponse}
          disabled={submitM.isPending}
          onSubmit={() => {
            if (!response.trim()) return;
            submitM.mutate({ response });
          }}
        />
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-ink-400">
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-ink-300"
            title="Exercise ID — for debugging"
          >
            #{exerciseId}
          </span>
          {/* Report button stays visible BEFORE and AFTER submit —
              a learner often only realises an exercise is broken
              after seeing the feedback panel. */}
          <ReportExerciseButton exerciseId={Number(exerciseId)} />
          <AutoAdvanceToggle
            on={autoAdvance}
            onToggle={() => setFlag("autoAdvance", !autoAdvance)}
          />
        </div>
        {/* Action affordances live only before an attempt is graded.
            Once we have a result, the only sensible next step is
            Next/Skip via the Feedback panel's own controls. */}
        {!lastResult && !answerRevealed && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {/* "I don't know" submits an empty response, which the
                grader marks AGAIN — this is the honest signal the
                scheduler needs. Skip discards the attempt entirely,
                which the scheduler can't learn from. Show-answer
                reveals the expected answer without grading the
                attempt — useful when you want to see the answer
                without polluting your history. */}
            <button
              onClick={() => {
                setResponse("");
                submitM.mutate({ response: "" });
              }}
              className="hover:text-ink-700"
              title="Honest 'I don't know' — the scheduler will resurface this sooner."
              disabled={submitM.isPending}
            >
              I don't know
            </button>
            <button
              onClick={() => setAnswerRevealed(true)}
              className="hover:text-ink-700"
              title="Reveal the expected answer. No grade is recorded."
            >
              Show answer
            </button>
            <button
              onClick={onNext}
              className="hover:text-ink-700"
              title="Pick a different exercise. No grade is recorded."
            >
              Skip →
            </button>
            {/* Admin-only one-tap "don't like" — fire-and-forget POST
                under category="disliked" and immediately advance. The
                report shows up under /admin/reports for review later.
                Stays out of normal-learner UI: this is Jorge's
                curation channel for on-the-go quality flagging, not
                a general user feature. */}
            {isAdmin && (
              <button
                onClick={() => {
                  void api.POST("/exercises/{exercise_id}/report", {
                    params: { path: { exercise_id: Number(exerciseId) } },
                    body: { category: "disliked" },
                  });
                  void onNext();
                }}
                className="hover:text-rose-700 text-rose-500"
                title="Flag for review and skip to the next exercise."
              >
                👎 Don't like
              </button>
            )}
          </div>
        )}
      </div>

      {debugScheduler && rationale && (
        <SchedulerDebugOverlay
          rationale={rationale}
          currentExerciseId={Number(exerciseId)}
        />
      )}
    </section>
  );
}

// Keep in sync with the exercise types seeded in
// apps/backend/src/lingua/seed/*.py. Missing keys fall through to
// the raw slug ("sentence_merge"), which Sara hit on /practice/1543
// in the 2026-05-23 feedback.
const EXERCISE_TYPE_LABELS: Record<string, string> = {
  build_question: "Build the question",
  categorize: "Categorize",
  classify: "Classify",
  cloze_passage: "Cloze passage",
  compound_split: "Split the compound",
  contrast: "Two-slot contrast",
  contrast_pair: "Contrast pair",
  dialogue: "Dialogue",
  error_correct: "Correct the error",
  fill_blank: "Fill the blank",
  match: "Match pairs",
  multiple_choice: "Multiple choice",
  odd_one_out: "Odd one out",
  order: "Order the words",
  paradigm: "Paradigm fill",
  sentence_merge: "Merge the sentences",
  substitution_drill: "Substitution drill",
  transform: "Transform",
  translate: "Translate",
  translate_back: "Translate back",
  true_false: "True / false",
  word_formation: "Word formation",
};

const SCAFFOLDING_LABELS = [
  "with options and hint",
  "with options",
  "with hint",
  "no hints",
];

// Chance the learner gets the exercise right purely by guessing — used
// to floor the displayed bar so an MC-with-4-options never shows below
// 25%, true/false never below 50%. Free-input formats stay at ~0.
function computeGuessRate(
  type: string,
  optionsShown: boolean,
  meta: ExerciseMetadata,
  isTrueFalse: boolean,
): number {
  if (isTrueFalse || type === "true_false") return 0.5;
  if (
    type === "multiple_choice" &&
    optionsShown &&
    Array.isArray(meta.options) &&
    meta.options.length >= 2
  ) {
    return 1 / meta.options.length;
  }
  return 0;
}

function ExerciseHeader({
  type,
  optionsShown,
  primarySkillCode,
  primarySkillName,
  primarySkillRetrievability,
  primarySkillStability,
  scaffoldingLevel,
  isFirstContact,
  guessRate,
  beliefSigma,
}: {
  type: string;
  optionsShown: boolean;
  primarySkillCode: string;
  primarySkillName: string;
  primarySkillRetrievability: number;
  primarySkillStability: number;
  scaffoldingLevel: number;
  isFirstContact: boolean;
  guessRate: number;
  beliefSigma: number | null;
}) {
  const baseLabel = EXERCISE_TYPE_LABELS[type] ?? type;
  const typeLabel =
    type === "multiple_choice" && !optionsShown
      ? `${baseLabel} · type the answer`
      : baseLabel;
  const scaffoldingLabel =
    SCAFFOLDING_LABELS[scaffoldingLevel] ?? SCAFFOLDING_LABELS[3];
  // K = knowledge-above-chance. K=0 means you'd do exactly what random
  // guessing gives on this format (max-entropy / zero info). K=1 means
  // certain recall. The bar shows K, not raw P(correct), so that a
  // 25%-correct rate on a 4-MC reads as an *empty* bar instead of a
  // 25%-full one.
  const knowledge =
    isFirstContact || guessRate >= 1
      ? 0
      : Math.max(0, (primarySkillRetrievability - guessRate) / (1 - guessRate));
  const knowledgePct = knowledge * 100;
  const showEmpty = isFirstContact;
  const barFill = showEmpty ? "bg-ink-200" : masteryBar(knowledge);
  // Approximate ±1σ epistemic-uncertainty band around K. The σ from the
  // backend is on the IRT logit-θ scale; we project it onto the K bar
  // via the logistic slope at the centre (≈ 0.25/unit) and clamp. This
  // is a first-pass visualisation — refining the geometry is queued.
  const sigmaKPct =
    beliefSigma != null && !isFirstContact
      ? Math.max(3, Math.min(40, beliefSigma * 25))
      : 0;
  const bandLeft = Math.max(0, knowledgePct - sigmaKPct);
  const bandRight = Math.min(100, knowledgePct + sigmaKPct);
  const showBand = sigmaKPct > 0 && !isFirstContact;
  const tooltip = isFirstContact
    ? "First time on this skill — no estimate yet."
    : showBand
      ? `Knowledge above chance: ${knowledgePct.toFixed(0)}% ` +
        `(± ${sigmaKPct.toFixed(0)}pp epistemic band). ` +
        `Wide band = the system isn't sure where you are; narrow = it is.`
      : `Knowledge above chance: ${knowledgePct.toFixed(0)}%. ` +
        `(0% = you'd do no better than guessing; 100% = certain.)`;
  return (
    <div className="mb-6 sm:mb-8 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-ink-400">
      <div className="flex items-center gap-2 flex-wrap">
        <span title={`scaffolding: ${scaffoldingLabel}`}>{typeLabel}</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <span title={primarySkillCode} className="truncate max-w-[60vw]">
          {primarySkillName}
        </span>
        <div
          className="flex items-center gap-1.5"
          title={tooltip}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={showEmpty ? 0 : Math.round(knowledgePct)}
          aria-label="how much you know above pure guessing"
        >
          <div className="relative h-1.5 w-16 rounded-full bg-ink-100 overflow-hidden">
            <div
              className={`h-full ${barFill}`}
              style={{ width: showEmpty ? "0%" : `${knowledgePct}%` }}
            />
            {showBand && (
              <div
                className="absolute inset-y-0 bg-ink-700/20"
                style={{
                  left: `${bandLeft}%`,
                  width: `${bandRight - bandLeft}%`,
                }}
                aria-hidden
              />
            )}
          </div>
          {isFirstContact && (
            <span className="text-[10px] text-ink-400">new</span>
          )}
        </div>
        <span
          className="tabular-nums text-ink-300"
          title="Stability: days until predicted recall (not the bar) drops to 90%."
        >
          S {formatDays(primarySkillStability)}
        </span>
        <MemoryStatsInfo />
      </div>
    </div>
  );
}

/**
 * Tiny clickable info-icon next to the `recall X% · S Yd` stats in the
 * practice header. On-screen affordance is the whole point — hover-only
 * tooltips don't render on touch devices and aren't discoverable on
 * desktop, so neither glyph explains itself today. Popover dismisses on
 * outside click / Escape / link follow. No external dep.
 */
/**
 * Click-to-open popover that lets the learner flag a content issue on
 * the current exercise (wrong answer / unclear / broken / other +
 * optional free-text note). Submits via POST `/exercises/{id}/report`.
 * No backend admin UI yet — Jorge reads the rows out of Postgres.
 *
 * Stays visible across the pre-submit and post-submit states because
 * learners often only realise an exercise is broken *after* seeing
 * the feedback panel and the expected answer.
 */
type ReportCategory = "wrong_answer" | "unclear" | "broken" | "other";

const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  wrong_answer: "Wrong answer",
  unclear: "Unclear / ambiguous",
  broken: "Broken / missing content",
  other: "Other",
};

function ReportExerciseButton({ exerciseId }: { exerciseId: number }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<ReportCategory>("broken");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset transient form state whenever the popover closes so a
  // re-open doesn't reveal a stale "sent" message.
  useEffect(() => {
    if (!open) {
      setStatus("idle");
      setNote("");
      setCategory("broken");
    }
  }, [open]);

  const submit = async () => {
    setStatus("sending");
    try {
      const trimmed = note.trim();
      const { error } = await api.POST("/exercises/{exercise_id}/report", {
        params: { path: { exercise_id: exerciseId } },
        body: trimmed ? { category, note: trimmed } : { category },
      });
      if (error) throw error;
      setStatus("sent");
      // Auto-dismiss the popover after a beat so the learner sees the
      // confirmation, then gets back to practicing.
      window.setTimeout(() => setOpen(false), 1200);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="hover:text-ink-700 text-ink-400"
        title="Report a problem with this exercise"
      >
        Report
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute left-0 top-6 z-20 w-72 border border-ink-300 bg-paper p-3 text-left text-ink-700"
        >
          {status === "sent" ? (
            <div className="text-sm text-emerald-700">
              Thanks — flagged for review.
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wide text-ink-400 mb-2">
                Report exercise #{exerciseId}
              </div>
              <fieldset className="space-y-1.5 mb-2">
                {(Object.keys(REPORT_CATEGORY_LABELS) as ReportCategory[]).map(
                  (key) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="report-category"
                        value={key}
                        checked={category === key}
                        onChange={() => setCategory(key)}
                      />
                      <span className="text-ink-700">
                        {REPORT_CATEGORY_LABELS[key]}
                      </span>
                    </label>
                  ),
                )}
              </fieldset>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="Optional note (max 500 chars)"
                rows={3}
                className="w-full text-sm border border-ink-200 rounded p-1.5 mb-2 focus:border-ink-900 focus:outline-none"
              />
              {status === "error" && (
                <div className="text-xs text-rose-600 mb-2">
                  Couldn't send. Try again in a moment.
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs text-ink-400 hover:text-ink-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={status === "sending"}
                  className="text-xs rounded-md bg-accent px-3 py-1.5 text-accent-fg hover:bg-accent-hover disabled:opacity-50"
                >
                  {status === "sending" ? "Sending…" : "Report"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MemoryStatsInfo() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="What do these stats mean?"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none transition-colors ${
          open
            ? "border-ink-900 text-ink-900"
            : "border-ink-300 text-ink-400 hover:border-ink-700 hover:text-ink-700"
        }`}
      >
        ?
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-6 z-20 w-72 border border-ink-300 bg-paper p-3 text-left text-ink-700"
        >
          <dl className="space-y-2 text-xs leading-snug">
            <div>
              <dt className="font-medium text-ink-900">the bar</dt>
              <dd className="text-ink-700">
                Knowledge above pure guessing. <strong>Empty</strong> = you'd do
                no better than random chance for this format (max-entropy / no
                information). <strong>Full</strong> = certain recall. Defined as{" "}
                <code>(P − g) / (1 − g)</code>, where <code>P</code> is the raw
                probability the memory model gives you and <code>g</code> is the
                format's guess rate (≈ 0 for free input, 1/k for k-option MC,
                0.5 for true/false).
              </dd>
            </div>
            <div>
              <dt className="font-medium text-ink-900">
                S <span className="text-ink-400">(stability)</span>
              </dt>
              <dd className="text-ink-700">
                Memory durability in days. Specifically: the number of days from
                your last review at which <code>P</code> drops to 90%. Correct
                after a real wait grows S; wrong shrinks it. Bigger S → memory
                decays more slowly.
              </dd>
            </div>
          </dl>
          <div className="mt-3 pt-2 border-t border-ink-100">
            <Link
              to="/about"
              onClick={() => setOpen(false)}
              className="text-xs text-ink-900 hover:text-ink-700 underline underline-offset-2 decoration-1"
            >
              How the engine works →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function SchedulerDebugOverlay({
  rationale,
  currentExerciseId,
}: {
  rationale: SchedulerRationale;
  currentExerciseId: number;
}) {
  const top = (rationale.actionSpace ?? []).slice(0, 8);
  // Editorial-academic chrome (ink-only hairline; no fill; square
  // corners) per design.md 2026-05-23 §3a. Default collapsed — opens
  // only when a dev clicks the summary; never eats first-fold space on
  // /practice in production.
  return (
    <details className="mb-6 border border-ink-100 bg-paper text-ink-700">
      <summary className="cursor-pointer px-4 py-2 text-xs uppercase tracking-wide text-ink-400">
        Agent debug overlay
      </summary>
      <div className="px-4 pb-4 space-y-3 text-xs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
          <Stat
            label="Winning signal"
            value={rationale.winningSignal}
            title="bellman when the planner picked; overdue when a past-due review override fired"
          />
          <Stat
            label="Q̂(b, a) of pick"
            value={(rationale.qHat ?? 0).toFixed(2)}
            title="Expected cumulative discounted reward of the chosen action under the current belief"
          />
          <Stat
            label="V̂(b) = max_a Q̂"
            value={(rationale.vHat ?? 0).toFixed(2)}
            title="Best achievable value under any action in the action space"
          />
          <Stat
            label="Overdue?"
            value={rationale.overdue ? "yes" : "no"}
            title="Past scheduled review date by more than the grace period"
          />
        </div>

        <div>
          <div className="uppercase tracking-wide mb-1 opacity-70">
            Action space (top {top.length} of{" "}
            {rationale.actionSpace?.length ?? 0} — 4 scaffolding levels ×
            eligible skills)
          </div>
          <ul className="space-y-1">
            {top.map((row) => {
              const isCurrent =
                row.exerciseId === currentExerciseId && row.isChosen;
              const scaffold = SCAFFOLD_LABEL[row.scaffoldingLevel] ?? "?";
              return (
                <li
                  key={`${row.skillCode}-${row.exerciseId}-${row.scaffoldingLevel}`}
                  className={`grid grid-cols-12 items-center gap-2 px-2 py-1 ${
                    isCurrent ? "bg-ink-100 font-medium text-ink-900" : ""
                  }`}
                >
                  <span className="col-span-1">{row.isChosen ? "★" : ""}</span>
                  <span
                    className="col-span-4 font-mono truncate text-[11px]"
                    title={row.skillCode}
                  >
                    {row.skillCode}
                  </span>
                  <span
                    className="col-span-2 text-[11px] opacity-80"
                    title="scaffolding action: 0 options+hint, 1 options, 2 hint, 3 cold"
                  >
                    {scaffold}
                  </span>
                  <span
                    className="col-span-3 text-right tabular-nums"
                    title="Bellman Q̂ — expected cumulative discounted reward in bits"
                  >
                    Q̂ {(row.qHat ?? 0).toFixed(2)}
                  </span>
                  <span
                    className="col-span-2 text-right tabular-nums opacity-80"
                    title="one-step expected reward = binary entropy of P(correct)"
                  >
                    {row.expectedRewardBits.toFixed(2)}b
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="opacity-70">
          Bellman planner: Q̂ estimated by Monte Carlo rollouts — at each step
          the learner's response is sampled under the scaffolded observation
          model and the memory trace updated. Reward per step is information
          bits (self-information of the observed outcome under the predicted
          distribution), so easy scaffolded successes earn near-zero. 24
          trajectories, γ=0.95, horizon=5, action space enumerates 4 scaffolding
          levels per eligible skill.
        </div>
      </div>
    </details>
  );
}

function Stat({
  label,
  value,
  title,
}: {
  label: string;
  value: string;
  title?: string;
}) {
  return (
    <div title={title}>
      <div className="opacity-60">{label}</div>
      <div className="font-serif text-base tabular-nums">{value}</div>
    </div>
  );
}

function formatDays(days: number): string {
  if (!isFinite(days) || days <= 0) return "—";
  if (days < 1) return `${Math.round(days * 24)}h`;
  if (days < 14) return `${days.toFixed(1)}d`;
  if (days < 60) return `${Math.round(days)}d`;
  return `${(days / 30).toFixed(1)}mo`;
}

// Suppress rows where the FSRS lapse clamp (`min(S_lapse, S)`) left S
// unchanged, or where a same-session correct update barely moves the
// needle — those rows are visual noise that confuse rather than inform.
function stabilityChanged(u: {
  stabilityBefore: number;
  stabilityAfter: number;
}): boolean {
  return Math.abs(u.stabilityAfter - u.stabilityBefore) > 1e-3;
}

function formatStabilityTransition(
  before: number,
  after: number,
  passed: boolean,
): ReactNode {
  const isFirstContact = before <= 0;
  if (isFirstContact) {
    return (
      <>
        <span className="text-ink-300">new</span>{" "}
        <span className="text-ink-400">→</span> {formatDays(after)}
      </>
    );
  }
  // FSRS-5 can shrink stability on a CORRECT answer when the grade is
  // HARD (scaffolded success) or when the recency-elapsed factor
  // dominates — this is mathematically expected but it reads as the
  // system punishing success without a tooltip. Diego and Yuki both
  // flagged it in 2026-05-23 feedback. When passed=true but
  // after<before, render a muted down-arrow + tooltip explaining the
  // expected behavior so the learner stops reading it as a bug.
  const shrinkOnSuccess = passed && after < before;
  const arrowClass = !passed
    ? "text-rose-600"
    : shrinkOnSuccess
      ? "text-ink-300"
      : "text-emerald-600";
  const tooltip = shrinkOnSuccess
    ? "Small stability drop on a correct answer is expected — FSRS-5 down-weights scaffolded successes and shrinks unused-but-fresh memory. The model still scheduled it further out than the elapsed gap."
    : undefined;
  return (
    <span title={tooltip}>
      {formatDays(before)} <span className={arrowClass}>→</span>{" "}
      {formatDays(after)}
      {shrinkOnSuccess && (
        <span className="ml-1 text-ink-300 text-xs" aria-hidden>
          ⓘ
        </span>
      )}
    </span>
  );
}

// Continuous CEFR axis: A1=1.0, A2=2.0, ..., C2=6.0. Matches the backend
// `CEFR_POSITION` constant in services/cefr_mapping.py — keep in sync.
const CEFR_LABELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

function cefrPositionToPercent(pos: number): number {
  // Clamp [1.0, 6.0] → [0%, 100%].
  const clamped = Math.max(1, Math.min(6, pos));
  return ((clamped - 1) / 5) * 100;
}

function beliefDeltaWord(
  before: BeliefSnapshot | null,
  after: BeliefSnapshot | null,
): string | null {
  if (!before || !after) return null;
  const delta = after.cefrCenter - before.cefrCenter;
  if (Math.abs(delta) < 0.02) return null;
  if (delta > 0.15) return "stepped up";
  if (delta > 0) return "edged up";
  if (delta < -0.15) return "stepped down";
  return "edged down";
}

function BeliefWhisker({
  before,
  after,
}: {
  before: BeliefSnapshot | null;
  after: BeliefSnapshot | null;
}) {
  if (!after) return null;
  const deltaWord = beliefDeltaWord(before, after);
  const sharperMsg =
    before && after.sigma < before.sigma * 0.95
      ? "sharper read"
      : before && after.sigma > before.sigma * 1.05
        ? "less certain"
        : null;
  return (
    <div className="mt-1 flex flex-col gap-0.5">
      <svg
        viewBox="0 0 200 22"
        className="w-full max-w-[280px] h-5"
        role="img"
        aria-label={`Belief whisker: ${after.caption}`}
      >
        {/* Axis baseline */}
        <line
          x1="0"
          y1="18"
          x2="200"
          y2="18"
          stroke="currentColor"
          strokeOpacity="0.15"
        />
        {/* Tick marks + labels */}
        {CEFR_LABELS.map((label, idx) => {
          const x = (idx / 5) * 200;
          return (
            <g key={label}>
              <line
                x1={x}
                y1="14"
                x2={x}
                y2="18"
                stroke="currentColor"
                strokeOpacity="0.25"
              />
              <text
                x={x}
                y={9}
                fontSize="6"
                fill="currentColor"
                fillOpacity="0.35"
                textAnchor="middle"
              >
                {label}
              </text>
            </g>
          );
        })}
        {/* Before whisker (lighter, behind) */}
        {before && (
          <g>
            <line
              x1={cefrPositionToPercent(before.cefrLow) * 2}
              y1="13"
              x2={cefrPositionToPercent(before.cefrHigh) * 2}
              y2="13"
              stroke="currentColor"
              strokeOpacity="0.25"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle
              cx={cefrPositionToPercent(before.cefrCenter) * 2}
              cy="13"
              r="2"
              fill="currentColor"
              fillOpacity="0.3"
            />
          </g>
        )}
        {/* After whisker (primary) */}
        <line
          x1={cefrPositionToPercent(after.cefrLow) * 2}
          y1="13"
          x2={cefrPositionToPercent(after.cefrHigh) * 2}
          y2="13"
          stroke="#0ea5e9"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle
          cx={cefrPositionToPercent(after.cefrCenter) * 2}
          cy="13"
          r="2.5"
          fill="#0ea5e9"
        />
      </svg>
      <div className="text-[10px] text-ink-400 leading-tight">
        {after.caption}
        {deltaWord && <span className="ml-1 italic">— {deltaWord}</span>}
        {sharperMsg && <span className="ml-1 italic">· {sharperMsg}</span>}
      </div>
    </div>
  );
}

function Prompt({
  meta,
  fallback,
  hideSentence = false,
}: {
  meta: ExerciseMetadata;
  fallback: string;
  // When the answer input renders the sentence inline (multi-blank
  // mode), skip the sentence here to avoid showing it twice. The
  // instruction + gloss still belong above the input.
  hideSentence?: boolean;
}) {
  // Pick the best "main text" for this exercise. true_false uses
  // `claim`; everything else uses `sentence`; if neither, fall back
  // to the raw `exercise.prompt`. Previously a true_false with a
  // truthy `gloss = "—"` slipped past the early branch and rendered
  // only the em-dash with no claim text at all (bug seen on #1487).
  const mainText = !hideSentence
    ? (meta.sentence ?? meta.claim ?? fallback)
    : "";
  if (meta.instruction || mainText || meta.gloss) {
    return (
      <div className="mb-8">
        {/* Three visually distinct registers:
              1. Instruction = small uppercase eyebrow (sans, ink-400,
                 tracked + uppercase). Procedural — "what kind of
                 answer am I after".
              2. Gloss = italic, left-bordered amber accent. "Here's
                 what the sentence means — express this." Italic +
                 colour + border together make it instantly
                 distinguishable from the instruction above.
              3. Main sentence (German with blank) = big serif, ink-900.
                 The answer surface.
            Before this, instruction and gloss were both plain sans in
            two shades of grey, which Jorge correctly read as one
            indistinguishable block. */}
        {meta.instruction && <p className="eyebrow mb-3">{meta.instruction}</p>}
        {meta.gloss &&
          meta.gloss !== "—" &&
          mainText &&
          mainText !== meta.gloss && (
            <p
              className="text-lg sm:text-xl text-amber-900 italic mb-4 leading-snug border-l-2 border-amber-300 pl-3"
              title="The meaning. Express this in German below."
            >
              {meta.gloss}
            </p>
          )}
        {mainText && (
          <p className="font-serif text-2xl sm:text-3xl text-ink-900 mb-2 leading-relaxed break-words">
            {mainText}
          </p>
        )}
        {/* If the gloss is the ONLY content (no mainText), render it
            as the prominent line instead. Otherwise the gloss above
            already showed it. */}
        {meta.gloss && meta.gloss !== "—" && !mainText && (
          <p className="font-serif text-2xl sm:text-3xl text-ink-900 leading-relaxed">
            {meta.gloss}
          </p>
        )}
      </div>
    );
  }
  return (
    <h2 className="font-serif text-xl sm:text-2xl text-ink-900 mb-6 sm:mb-8 leading-relaxed break-words">
      {fallback}
    </h2>
  );
}

function MultipleChoice({
  options,
  disabled,
  onPick,
  onKeySelect,
  onKeySubmit,
  selected,
}: {
  options: readonly string[];
  disabled: boolean;
  // Click handler: selects AND submits (mouse users want one-action
  // commit).
  onPick: (choice: string) => void;
  // Keyboard select: 1/2/3/4 highlights an option without committing.
  // Separated from onPick so a stray numeric keypress can't ship an
  // answer the learner didn't intend (2026-05-23 ux feedback §E11/H7
  // "twitch-mistake"). Falls back to onPick if not provided.
  onKeySelect?: (choice: string) => void;
  // Enter on a highlighted option commits.
  onKeySubmit?: () => void;
  selected?: string;
}) {
  // Keyboard: 1/2/3/4 highlights the corresponding option (no commit);
  // Enter commits the highlighted answer. The small numeral in each
  // button's corner makes the affordance visible. Ignored while a
  // text input is focused.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      )
        return;
      if (e.key === "Enter" && selected && onKeySubmit) {
        e.preventDefault();
        onKeySubmit();
        return;
      }
      const idx = Number(e.key) - 1;
      if (idx >= 0 && idx < options.length) {
        e.preventDefault();
        (onKeySelect ?? onPick)(options[idx]!);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [disabled, onPick, onKeySelect, onKeySubmit, options, selected]);

  return (
    <div className="grid grid-cols-2 gap-3">
      {options.map((opt, i) => {
        const isSelected = selected === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onPick(opt)}
            disabled={disabled}
            className={`relative rounded-md border-2 px-4 sm:px-5 py-4 text-base sm:text-lg text-ink-900 transition-colors disabled:opacity-50 break-words ${
              isSelected
                ? "border-ink-900 bg-ink-100"
                : "border-ink-200 hover:border-ink-900 hover:bg-ink-100"
            }`}
          >
            <span className="absolute left-2 top-1.5 text-[10px] font-mono text-ink-400">
              {i + 1}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function parseMatchPairs(
  expected: string,
): { source: string; target: string }[] | null {
  // Format: "der Bruder:brother|die Mutter:mother|..."
  const segments = expected.split("|");
  const pairs: { source: string; target: string }[] = [];
  for (const seg of segments) {
    const idx = seg.indexOf(":");
    if (idx <= 0) return null;
    pairs.push({
      source: seg.slice(0, idx).trim(),
      target: seg.slice(idx + 1).trim(),
    });
  }
  return pairs.length >= 2 ? pairs : null;
}

function MatchInput({
  pairs,
  disabled,
  onSubmit,
}: {
  pairs: { source: string; target: string }[];
  disabled: boolean;
  onSubmit: (joinedResponse: string) => void;
}) {
  // Targets shown to the user as the dropdown options. Sorted so
  // option-order isn't a tell for the answer order. The grader
  // compares the WHOLE response string to `expected` exactly, so we
  // submit pairs in the canonical (seeded) source order to match.
  const targetOptions = [...new Set(pairs.map((p) => p.target))].sort((a, b) =>
    a.localeCompare(b),
  );
  const [picks, setPicks] = useState<string[]>(() => pairs.map(() => ""));
  const allPicked = picks.every((p) => p.length > 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!allPicked) return;
        const joined = pairs.map((p, i) => `${p.source}:${picks[i]}`).join("|");
        onSubmit(joined);
      }}
      className="space-y-4"
    >
      <ul className="space-y-2">
        {pairs.map((pair, i) => (
          <li
            key={pair.source}
            className="flex flex-wrap items-center gap-3 border border-ink-100 rounded-md bg-paper px-3 py-3"
          >
            <span className="font-serif text-lg text-ink-900 min-w-0 flex-1">
              {pair.source}
            </span>
            <span aria-hidden className="text-ink-300">
              →
            </span>
            <StyledPicker
              value={picks[i] ?? ""}
              disabled={disabled}
              options={targetOptions}
              ariaLabel={`Match for ${pair.source}`}
              placeholder="pick"
              onChange={(value) => {
                const next = [...picks];
                next[i] = value;
                setPicks(next);
              }}
            />
          </li>
        ))}
      </ul>
      <button
        type="submit"
        disabled={disabled || !allPicked}
        className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
      >
        {disabled ? "Grading…" : "Submit"}
      </button>
    </form>
  );
}

/**
 * Custom dropdown picker. Replaces the native `<select>` in
 * match-pairs (and any other block-level exercise input that wants a
 * picker) with a styled button + popover list that matches the
 * editorial-academic chrome rather than the browser default.
 *
 * Intentionally *not* a generic dropdown library — the API is narrow
 * (value/options/onChange + a label and placeholder) so call sites
 * can swap in without thinking. Inline-in-sentence pickers (Contrast)
 * have different positioning needs and stay as native `<select>` for
 * now; they're a separate refactor.
 *
 * Accessibility: button role + aria-expanded, listbox role + option
 * children, keyboard support (Up/Down to move, Enter to pick,
 * Escape to dismiss, type-ahead via the first letter).
 */
function StyledPicker({
  value,
  options,
  disabled,
  ariaLabel,
  placeholder,
  onChange,
  variant = "block",
}: {
  value: string;
  options: readonly string[];
  disabled: boolean;
  ariaLabel: string;
  placeholder: string;
  onChange: (next: string) => void;
  // "block" = field-style button (used in match-pair list rows).
  // "inline" = sits inside running prose (used in contrast
  // exercises). Only the trigger chrome differs; the popover list
  // is identical between the two.
  variant?: "block" | "inline";
}) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Sync highlight to the currently-selected value whenever we open.
  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o === value);
    setHighlightIdx(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  // Outside-click / Escape dismissal. Same shape as the other popovers.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[highlightIdx];
      if (opt != null) pick(opt);
    } else if (e.key.length === 1) {
      // Type-ahead — jump to the first option starting with that char.
      const c = e.key.toLowerCase();
      const idx = options.findIndex((o) => o.toLowerCase().startsWith(c));
      if (idx >= 0) setHighlightIdx(idx);
    }
  };

  const inline = variant === "inline";
  // The inline trigger replaces the bordered field-button with an
  // underline-only baseline-aligned glyph that sits inside the
  // sentence flow. The popover list (below) is identical between
  // the two variants; only the trigger chrome changes.
  const triggerClass = inline
    ? `inline-flex items-baseline gap-1 border-b-2 mx-1 px-0.5 align-baseline bg-transparent transition-colors disabled:opacity-50 ${
        open
          ? "border-ink-900 text-ink-900"
          : value
            ? "border-ink-200 hover:border-ink-700 text-ink-900"
            : "border-ink-200 hover:border-ink-700 text-ink-400"
      }`
    : `inline-flex items-center justify-between gap-2 min-w-[10ch] rounded-md border px-3 py-2 text-base bg-paper transition-colors disabled:opacity-50 ${
        open
          ? "border-ink-900"
          : value
            ? "border-ink-200 hover:border-ink-700 text-ink-900"
            : "border-ink-200 hover:border-ink-700 text-ink-400"
      }`;
  return (
    <div
      className={inline ? "relative inline-block" : "relative"}
      ref={wrapRef}
    >
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={triggerClass}
      >
        <span className={inline ? "font-serif" : "font-serif"}>
          {value || (
            <span className="text-ink-300">
              {inline ? `…${placeholder}…` : `— ${placeholder} —`}
            </span>
          )}
        </span>
        {!inline && (
          <span aria-hidden className="text-ink-300 text-[10px]">
            ▾
          </span>
        )}
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className={`absolute top-full mt-1 z-20 min-w-full w-max max-w-[14rem] border border-ink-300 bg-paper max-h-64 overflow-auto ${
            inline ? "left-0" : "right-0"
          }`}
        >
          {options.map((opt, i) => {
            const isActive = i === highlightIdx;
            const isSelected = opt === value;
            return (
              <li
                key={opt}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlightIdx(i)}
                onClick={() => pick(opt)}
                className={`px-3 py-2 cursor-pointer font-serif text-base ${
                  isActive ? "bg-ink-100 text-ink-900" : "text-ink-700"
                } ${isSelected ? "font-medium" : ""}`}
              >
                {opt}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TrueFalseInput({
  disabled,
  onPick,
  onKeySelect,
  onKeySubmit,
  selected,
}: {
  disabled: boolean;
  // Click handler: selects AND submits.
  onPick: (value: "true" | "false") => void;
  // Keyboard select: T/F/1/2 highlights without committing.
  // Separated from onPick (2026-05-23 ux feedback §E11/H7 — pressing
  // `2` while inspecting the page used to ship an answer). Falls back
  // to onPick if not provided.
  onKeySelect?: (value: "true" | "false") => void;
  // Enter on a highlighted option commits.
  onKeySubmit?: () => void;
  selected?: string;
}) {
  // Keyboard: T / F (and 1 / 2) highlight; Enter commits. Mirrors the
  // multiple-choice pattern so the user never reaches for the mouse
  // in a streak of drills.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      )
        return;
      if (e.key === "Enter" && selected && onKeySubmit) {
        e.preventDefault();
        onKeySubmit();
        return;
      }
      const k = e.key.toLowerCase();
      const select = onKeySelect ?? onPick;
      if (k === "t" || k === "1") select("true");
      else if (k === "f" || k === "2") select("false");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [disabled, onPick, onKeySelect, onKeySubmit, selected]);

  return (
    <div className="grid grid-cols-2 gap-3">
      {(["true", "false"] as const).map((v, i) => {
        const isSelected = selected === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onPick(v)}
            disabled={disabled}
            className={`relative rounded-md border-2 px-5 py-4 text-base sm:text-lg text-ink-900 transition-colors disabled:opacity-50 capitalize ${
              isSelected
                ? "border-ink-900 bg-ink-100"
                : "border-ink-200 hover:border-ink-900 hover:bg-ink-100"
            }`}
          >
            <span className="absolute left-2 top-1.5 text-[10px] font-mono text-ink-400">
              {i + 1}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}

function ContrastInput({
  slots,
  meta,
  disabled,
  onSubmit,
}: {
  slots: { source: string; target: string }[];
  meta: ExerciseMetadata;
  disabled: boolean;
  onSubmit: (joinedResponse: string) => void;
}) {
  // Contrast metadata carries a_sentence + b_sentence (with `___` in
  // each) and a `forms` array of the two values to choose between.
  // Render each sentence inline with a dropdown in place of the
  // `___`. Falls back to the bare slot labels if metadata is missing.
  //
  // `forms` ships in two shapes — old seeds use bare strings
  // (`["mir","mich"]`), newer seeds use `[{label,value,meaning}]`
  // objects. Normalise to strings here so StyledPicker, which renders
  // its options directly into JSX, doesn't get an object child.
  const formOptions =
    Array.isArray(meta.forms) && meta.forms.length > 0
      ? meta.forms.map((f) => (typeof f === "string" ? f : f.value))
      : [...new Set(slots.map((s) => s.target))].sort((a, b) =>
          a.localeCompare(b),
        );
  const [picks, setPicks] = useState<string[]>(() => slots.map(() => ""));
  const allPicked = picks.every((p) => p.length > 0);

  const sentenceFor = (i: number): string | null => {
    if (i === 0 && meta.a_sentence) return meta.a_sentence;
    if (i === 1 && meta.b_sentence) return meta.b_sentence;
    return null;
  };
  const glossFor = (i: number): string | null => {
    if (i === 0 && meta.gloss_a) return meta.gloss_a;
    if (i === 1 && meta.gloss_b) return meta.gloss_b;
    return null;
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!allPicked) return;
        const joined = slots.map((s, i) => `${s.source}:${picks[i]}`).join("|");
        onSubmit(joined);
      }}
      className="space-y-4"
    >
      <ul className="space-y-3">
        {slots.map((slot, i) => {
          const sentence = sentenceFor(i);
          const gloss = glossFor(i);
          const segments = sentence ? sentence.split("___") : null;
          return (
            <li
              key={slot.source}
              className="border border-ink-100 rounded-md bg-paper px-3 py-3"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs uppercase text-ink-400">
                  {slot.source}
                </span>
              </div>
              {segments && segments.length >= 2 ? (
                <p className="font-serif text-lg sm:text-xl text-ink-900 leading-relaxed break-words">
                  {segments.map((seg, k) => (
                    <span key={k}>
                      {seg}
                      {k < segments.length - 1 && (
                        <StyledPicker
                          variant="inline"
                          value={picks[i] ?? ""}
                          disabled={disabled}
                          options={formOptions}
                          ariaLabel={`Choice for ${slot.source}`}
                          placeholder="pick"
                          onChange={(value) => {
                            const next = [...picks];
                            next[i] = value;
                            setPicks(next);
                          }}
                        />
                      )}
                    </span>
                  ))}
                </p>
              ) : (
                <StyledPicker
                  value={picks[i] ?? ""}
                  disabled={disabled}
                  options={formOptions}
                  ariaLabel={`Choice for ${slot.source}`}
                  placeholder="pick"
                  onChange={(value) => {
                    const next = [...picks];
                    next[i] = value;
                    setPicks(next);
                  }}
                />
              )}
              {gloss && (
                <p className="text-xs text-ink-400 italic mt-1">{gloss}</p>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="submit"
        disabled={disabled || !allPicked}
        className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
      >
        {disabled ? "Grading…" : "Submit"}
      </button>
    </form>
  );
}

function OrderInput({
  tokens,
  disabled,
  onSubmit,
}: {
  tokens: string[];
  disabled: boolean;
  onSubmit: (joinedResponse: string) => void;
}) {
  // Two zones: the SOURCE pool (untapped tokens) and the BUILD line
  // (tapped, in order). Tap a source token to push it onto build.
  // Tap a build token to pop it back. The submit response is the
  // built tokens joined with a single space.
  //
  // Tokens may repeat in the prompt (rare); we use indices into the
  // original array as keys, not the value strings, so duplicates
  // stay addressable.
  const [used, setUsed] = useState<number[]>([]); // indices in build order
  const available = tokens.map((_, i) => i).filter((i) => !used.includes(i));

  const built = used.map((i) => tokens[i]).join(" ");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (used.length === 0) return;
        onSubmit(built);
      }}
      className="space-y-4"
    >
      <div className="rounded-md border border-ink-200 bg-paper px-3 py-4 min-h-[3.5rem]">
        {used.length === 0 ? (
          <span className="text-ink-300 italic text-sm">
            Tap tokens below to build the sentence…
          </span>
        ) : (
          <p className="font-serif text-xl sm:text-2xl text-ink-900 leading-relaxed break-words">
            {used.map((idx, j) => (
              <button
                key={`b-${j}`}
                type="button"
                onClick={() => setUsed((u) => u.filter((_, k) => k !== j))}
                disabled={disabled}
                className="hover:bg-ink-100 rounded px-1 -mx-0.5 transition-colors"
                title="Tap to remove"
              >
                {tokens[idx]}
              </button>
            ))}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {available.length === 0 ? (
          <span className="text-ink-400 text-sm">All tokens used.</span>
        ) : (
          available.map((idx) => (
            <button
              key={`p-${idx}`}
              type="button"
              onClick={() => setUsed((u) => [...u, idx])}
              disabled={disabled}
              className="rounded-md border border-ink-200 bg-paper px-3 py-2 text-base text-ink-900 hover:border-ink-900 hover:bg-ink-100 transition-colors disabled:opacity-50"
            >
              {tokens[idx]}
            </button>
          ))
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={disabled || used.length === 0}
          className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {disabled ? "Grading…" : "Submit"}
        </button>
        {used.length > 0 && (
          <button
            type="button"
            onClick={() => setUsed([])}
            disabled={disabled}
            className="text-xs text-ink-400 hover:text-ink-700"
          >
            Reset
          </button>
        )}
      </div>
    </form>
  );
}

function ClozePassageInput({
  passage,
  disabled,
  onSubmit,
}: {
  passage: string;
  disabled: boolean;
  onSubmit: (joinedResponse: string) => void;
}) {
  // Split on `(N)___` markers. Result alternates: text, blank, text,
  // blank, ... so segments.length === blankCount + 1.
  const segments = passage.split(/\(\d+\)___/);
  const blankCount = segments.length - 1;
  const [values, setValues] = useState<string[]>(() =>
    Array.from({ length: blankCount }, () => ""),
  );
  const allFilled = values.every((v) => v.trim().length > 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!allFilled) return;
        // Grader expects pipe-separated answers in blank order.
        onSubmit(values.map((v) => v.trim()).join("|"));
      }}
      className="space-y-4"
    >
      <p className="text-sm text-ink-400 uppercase tracking-wide">
        Fill {blankCount} blanks
      </p>
      <p className="font-serif text-xl sm:text-2xl text-ink-900 leading-relaxed break-words">
        {segments.map((seg, i) => (
          <span key={i}>
            {seg}
            {i < blankCount && (
              <>
                <sup className="text-xs text-ink-400 font-mono align-super mr-0.5">
                  {i + 1}
                </sup>
                <input
                  autoFocus={i === 0}
                  value={values[i]}
                  onChange={(e) => {
                    const next = [...values];
                    next[i] = e.target.value;
                    setValues(next);
                  }}
                  disabled={disabled}
                  aria-label={`Blank ${i + 1} of ${blankCount}`}
                  className="inline-block border-b-2 border-ink-200 focus:border-ink-900 focus:outline-none bg-transparent px-1 mx-0.5 text-ink-900 align-baseline w-[6ch] sm:w-[8ch] text-center"
                  size={Math.max(4, values[i]?.length || 0)}
                />
              </>
            )}
          </span>
        ))}
      </p>
      <button
        type="submit"
        disabled={disabled || !allFilled}
        className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
      >
        {disabled ? "Grading…" : "Submit"}
      </button>
    </form>
  );
}

function MultiBlankInput({
  sentence,
  disabled,
  onSubmit,
}: {
  sentence: string;
  disabled: boolean;
  onSubmit: (joinedResponse: string) => void;
}) {
  // Split on the literal `___` token. Result: [text, text, text, ...]
  // with one fewer text segments than there are blanks at the seams.
  // Render each segment inline; between segments, render an input.
  const segments = sentence.split("___");
  const blankCount = segments.length - 1;
  const [values, setValues] = useState<string[]>(() =>
    Array.from({ length: blankCount }, () => ""),
  );

  const allFilled = values.every((v) => v.trim().length > 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!allFilled) return;
        onSubmit(values.map((v) => v.trim()).join(" "));
      }}
      className="space-y-4"
    >
      {/* Sentence rendered inline with inputs in place of each `___`.
          The font matches the prompt typography (serif, text-2xl/3xl)
          so the answer feels embedded in the sentence, not detached. */}
      <p className="font-serif text-2xl sm:text-3xl text-ink-900 leading-relaxed break-words">
        {segments.map((seg, i) => (
          <span key={i}>
            {seg}
            {i < blankCount && (
              <input
                autoFocus={i === 0}
                value={values[i]}
                onChange={(e) => {
                  const next = [...values];
                  next[i] = e.target.value;
                  setValues(next);
                }}
                disabled={disabled}
                aria-label={`Blank ${i + 1} of ${blankCount}`}
                className="inline-block border-b-2 border-ink-200 focus:border-ink-900 focus:outline-none bg-transparent px-1 mx-0.5 text-ink-900 align-baseline tabular-nums w-[6ch] sm:w-[8ch] text-center"
                size={Math.max(4, values[i]?.length || 0)}
              />
            )}
          </span>
        ))}
      </p>
      <button
        type="submit"
        disabled={disabled || !allFilled}
        className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
      >
        {disabled ? "Grading…" : "Submit"}
      </button>
    </form>
  );
}

function TypedInput({
  value,
  onChange,
  disabled,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  onSubmit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your answer…"
        className="w-full rounded-md border border-ink-200 px-4 py-3 text-lg text-ink-900 focus:border-ink-900 focus:outline-none"
        disabled={disabled}
      />
      <button
        type="submit"
        disabled={disabled || !value.trim()}
        className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
      >
        {disabled ? "Grading…" : "Submit"}
      </button>
    </form>
  );
}

function Feedback({
  result,
  hint,
  onNext,
  autoAdvanceMs,
}: {
  result: GradingResult;
  hint: string | undefined;
  onNext: () => void;
  // Non-null when auto-advance is armed: the planner gave us this many
  // ms until it'll call onNext on its own. We render a shrinking
  // progress bar so the user sees what's about to happen and knows
  // they can tap to cancel.
  autoAdvanceMs: number | null;
}) {
  // An empty-string response means the user pressed "I don't know" — that's
  // an honest non-attempt, not a failed attempt. The scheduler still treats
  // it as AGAIN (so memory updates the same way), but the UI shouldn't scold.
  const honestSkip = !result.correct && result.response.trim() === "";
  const tone = result.correct
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : honestSkip
      ? "bg-ink-50 border-ink-200 text-ink-900"
      : "bg-rose-50 border-rose-200 text-rose-900";
  const headline = result.correct
    ? "Correct"
    : honestSkip
      ? "Noted — flagged for review"
      : "Not quite";
  return (
    <div className="space-y-4">
      <div className={`rounded-md px-4 py-3 border ${tone}`}>
        <div className="font-medium">{headline}</div>
        <div className="text-sm mt-2 space-y-1">
          {!honestSkip && (
            <div>
              <span className="opacity-60">Your answer:</span>{" "}
              <code className="font-mono bg-paper/50 px-1 rounded">
                {result.response || "(empty)"}
              </code>
            </div>
          )}
          {!result.correct && (
            <div>
              <span className="opacity-60">Expected:</span>{" "}
              <code className="font-mono bg-paper/50 px-1 rounded">
                {result.expected}
              </code>
            </div>
          )}
        </div>
        {result.feedback && (
          <div className="text-sm mt-2">{result.feedback}</div>
        )}
      </div>

      {!result.correct && hint && (
        <div className="rounded-md border border-ink-200 bg-paper px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-ink-400 mb-1">
            Why
          </div>
          <p className="text-sm text-ink-700 leading-snug">{hint}</p>
        </div>
      )}

      {result.transferFlags && result.transferFlags.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-amber-900 mb-2">
            L1 transfer detected
          </div>
          <ul className="space-y-2">
            {result.transferFlags.map((f) => (
              <li key={f.patternSlug} className="text-sm text-amber-900">
                <div className="font-medium">
                  <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">
                    {f.patternSlug}
                  </code>
                </div>
                {f.note && <div className="mt-1 italic">{f.note}</div>}
                <details className="mt-1">
                  <summary className="text-xs text-amber-700 cursor-pointer hover:text-amber-800">
                    Why this pattern?
                  </summary>
                  <div className="text-xs text-amber-800 mt-1 pl-2 border-l-2 border-amber-300">
                    {f.description}
                  </div>
                </details>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-ink-400">
            Skills
          </div>
          <div className="text-xs text-ink-400 tabular-nums">
            {formatSignedDays(result.retentionTimeGainedDays)} retention
          </div>
        </div>
        <ul className="space-y-1">
          {result.skillUpdates.filter(stabilityChanged).map((s) => (
            <li
              key={s.skillCode}
              className="flex flex-col gap-1 text-sm border-b border-ink-100 py-1.5"
            >
              <div className="flex flex-col gap-0.5 sm:grid sm:grid-cols-12 sm:items-center sm:gap-2">
                <span
                  className="col-span-6 text-ink-700 truncate"
                  title={s.skillCode}
                >
                  {s.skillName}
                </span>
                <span className="col-span-3 text-ink-400 tabular-nums">
                  {formatStabilityTransition(
                    s.stabilityBefore,
                    s.stabilityAfter,
                    s.passed,
                  )}
                </span>
                <span className="col-span-3 text-right text-ink-400 tabular-nums text-xs">
                  next in {formatDueIn(s.nextDueAt)}
                </span>
              </div>
              <BeliefWhisker
                before={s.beliefBefore ?? null}
                after={s.beliefAfter ?? null}
              />
            </li>
          ))}
        </ul>
      </div>

      {result.lexemeUpdates &&
        result.lexemeUpdates.filter(stabilityChanged).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-400 mb-2">
              Vocabulary
            </div>
            <ul className="space-y-1">
              {result.lexemeUpdates.filter(stabilityChanged).map((lx) => (
                <li
                  key={`${lx.lemma}-${lx.pos}-${lx.role}`}
                  className="flex flex-col gap-0.5 sm:grid sm:grid-cols-12 sm:items-center sm:gap-2 text-sm border-b border-ink-100 py-1.5"
                >
                  <span className="col-span-6 text-ink-700 truncate">
                    <span className="font-serif">{lx.lemma}</span>{" "}
                    <span className="text-xs uppercase text-ink-400">
                      {lx.pos}
                    </span>
                    {lx.role !== "target" && (
                      <span className="ml-1 text-xs text-ink-400">
                        ({lx.role})
                      </span>
                    )}
                  </span>
                  <span className="col-span-3 text-ink-400 tabular-nums">
                    {formatStabilityTransition(
                      lx.stabilityBefore,
                      lx.stabilityAfter,
                      lx.passed,
                    )}
                  </span>
                  <span className="col-span-3 text-right text-ink-400 tabular-nums text-xs">
                    next in {formatDueIn(lx.nextDueAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

      {result.constructionUpdates &&
        result.constructionUpdates.filter(stabilityChanged).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-400 mb-2">
              Phrases
            </div>
            <ul className="space-y-1">
              {result.constructionUpdates.filter(stabilityChanged).map((cx) => (
                <li
                  key={cx.form}
                  className="flex flex-col gap-0.5 sm:grid sm:grid-cols-12 sm:items-center sm:gap-2 text-sm border-b border-ink-100 py-1.5"
                >
                  <span className="col-span-6 text-ink-700 font-serif italic truncate">
                    {cx.form}
                  </span>
                  <span className="col-span-3 text-ink-400 tabular-nums">
                    {formatStabilityTransition(
                      cx.stabilityBefore,
                      cx.stabilityAfter,
                      cx.passed,
                    )}
                  </span>
                  <span className="col-span-3 text-right text-ink-400 tabular-nums text-xs">
                    next in {formatDueIn(cx.nextDueAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          autoFocus
          onClick={onNext}
          className="rounded-md bg-accent px-5 py-2.5 text-accent-fg hover:bg-accent-hover transition-colors"
        >
          Next exercise
        </button>
        {autoAdvanceMs != null && (
          <AutoAdvanceCountdown durationMs={autoAdvanceMs} />
        )}
      </div>
    </div>
  );
}

function AutoAdvanceToggle({
  on,
  onToggle,
}: {
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      aria-label={FLAGS.autoAdvance.label}
      title={FLAGS.autoAdvance.description}
      className={`inline-flex items-center gap-1.5 transition-colors ${
        on ? "text-ink-900" : "text-ink-400 hover:text-ink-700"
      }`}
    >
      {/* Small native-looking switch — pure CSS, no extra deps. Works
          the same on phone (tap) and desktop (click). The whole pill
          is a button so tap target is generous on mobile. */}
      <span
        className={`relative inline-block h-3 w-6 rounded-full transition-colors ${
          on ? "bg-ink-700" : "bg-ink-200"
        }`}
      >
        <span
          className={`absolute top-0.5 h-2 w-2 rounded-full bg-paper transition-all ${
            on ? "left-3.5" : "left-0.5"
          }`}
        />
      </span>
      <span>Auto-next</span>
    </button>
  );
}

function AutoAdvanceCountdown({ durationMs }: { durationMs: number }) {
  // Two-paint trick: render at full width on the first paint, then on
  // the next animation frame set width to 0 so the CSS transition
  // animates the bar shrinking over `durationMs`. The bar lives in a
  // flex row next to the "Next exercise" button, so any tap on the
  // page (caught at the window level by the parent's pointerdown
  // listener) cancels the timer and removes this component.
  const [width, setWidth] = useState(100);
  useEffect(() => {
    setWidth(100);
    const id = requestAnimationFrame(() => setWidth(0));
    return () => cancelAnimationFrame(id);
  }, [durationMs]);
  const seconds = (durationMs / 1000).toFixed(durationMs % 1000 === 0 ? 0 : 1);
  return (
    <div
      className="flex items-center gap-2 text-xs text-ink-400"
      title="Auto-advancing — tap anywhere or press any key to cancel."
    >
      <div className="relative h-1 w-24 overflow-hidden bg-ink-100 rounded-full">
        <div
          className="absolute inset-y-0 left-0 bg-ink-700"
          style={{
            width: `${width}%`,
            transition: `width ${durationMs}ms linear`,
          }}
        />
      </div>
      <span>Auto-next in ~{seconds}s</span>
    </div>
  );
}

function formatDueIn(iso: string | null): string {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (!isFinite(ms)) return "—";
  const days = ms / (1000 * 60 * 60 * 24);
  if (days < 0) return "now";
  return formatDays(days);
}

function formatSignedDays(days: number): string {
  if (!isFinite(days)) return "—";
  const sign = days >= 0 ? "+" : "−";
  const abs = Math.abs(days);
  if (abs < 1) return `${sign}${(abs * 24).toFixed(1)}h`;
  return `${sign}${abs.toFixed(1)}d`;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-2xl px-6 py-24 text-center text-ink-700">
      {children}
    </section>
  );
}
